import { defineStore } from "pinia";
import {
  login,
  register,
  requestPasswordReset,
  loginWithGoogle,
  fetchMe,
} from "@/api/auth";
import {
  clearSessionIfCurrent,
  commitSessionToken,
  getSessionSnapshot,
  getStoredSessionToken,
} from "@/api/session-context.js";
import { invalidateShioriDestinationImageWorkflows } from "@/utils/ensure-shiori-destination-image.js";

export const useAuthStore = defineStore("auth", {
  state: () => ({
    token: getStoredSessionToken(),
    user: null,
    error: null,
  }),

  getters: {
    isAuthenticated: (state) => !!state.token,
    currentUser: (state) => state.user,
  },

  actions: {
    async login(email, password) {
      try {
        this.error = null;
        const response = await login(email, password);
        invalidateShioriDestinationImageWorkflows();
        this.token = response.access_token;
        commitSessionToken(this.token);

        // 削除済みアカウントの場合はユーザー情報を取得せずにレスポンスを返す
        if (response.deleted_account) {
          return response;
        }

        await this.fetchUser();
        return response;
      } catch (err) {
        this.error = err.response?.data?.detail || "ログインに失敗しました";
        console.error("Login error:", err);
        throw err;
      }
    },

    async register(email, password, user_name) {
      try {
        this.error = null;
        const response = await register(email, password, user_name);
        invalidateShioriDestinationImageWorkflows();
        this.token = response.access_token;
        commitSessionToken(this.token);

        // 削除済みアカウントの場合は、ユーザー情報を取得せずに復元情報を返す
        if (response.deleted_account) {
          return response;
        }

        await this.fetchUser();
        return response;
      } catch (err) {
        this.error = err.response?.data?.detail || "登録に失敗しました";
        console.error("Register error:", err);
        throw err;
      }
    },

    async requestPasswordReset(email) {
      try {
        this.error = null;
        await requestPasswordReset(email);
        return true;
      } catch (err) {
        this.error = err.response?.data?.detail || "メール送信に失敗しました";
        console.error("Password reset error:", err);
        throw err;
      }
    },

    async loginWithGoogle() {
      try {
        this.error = null;
        const response = await loginWithGoogle();
        return response;
      } catch (err) {
        this.error = err.response?.data?.detail || "Google認証に失敗しました";
        console.error("Google login error:", err);
        throw err;
      }
    },

    logout(expectedSession = null) {
      if (expectedSession && !clearSessionIfCurrent(expectedSession)) {
        return false;
      }
      invalidateShioriDestinationImageWorkflows();
      this.token = null;
      this.user = null;
      this.error = null;
      if (!expectedSession) commitSessionToken(null);
      return true;
    },

    async fetchUser() {
      if (this.token) {
        const requestedSession = getSessionSnapshot();
        try {
          this.user = await fetchMe(this.token);
          if (this.user?.HAS_PROFILE_IMAGE && this.user?.APP_USER_CODE) {
            const cacheBuster = Date.now();
            this.user.PROFILE_IMAGE_BASE64 =
              `${process.env.VUE_APP_API_URL || "/api"}/users/` +
              `${this.user.APP_USER_CODE}/profile-image?t=${cacheBuster}`;
          }
          return this.user;
        } catch (err) {
          console.error("Fetch user error:", err);
          // トークンが無効な場合はログアウト
          if (err.response?.status === 401) {
            const loggedOut = this.logout(requestedSession);
            if (!loggedOut && getStoredSessionToken() !== this.token) {
              this.token = null;
              this.user = null;
            }
          }
          return null;
        }
      }
      return null;
    },
  },
});
